import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { api } from "../../lib/api";
import {
  useCompletionStore,
  AGGRESSIVENESS_DEBOUNCE,
  LENGTH_MAX_TOKENS,
} from "../../stores/completion";
import { useAIStore } from "../../stores/ai";

const ghostTextPluginKey = new PluginKey("docxGhostText");

function getPlainText(doc: any): string {
  const chunks: string[] = [];
  doc.descendants((node: any) => {
    if (node.isText) chunks.push(node.text!);
    else if (node.isBlock && chunks.length > 0) chunks.push("\n");
  });
  return chunks.join("");
}

function getTextOffsetAtPos(doc: any, pos: number): number {
  let offset = 0;
  let found = false;

  doc.nodesBetween(0, pos, (node: any, nodePos: number) => {
    if (found) return false;
    if (node.isText) {
      const start = nodePos;
      const end = nodePos + node.nodeSize;
      if (end <= pos) {
        offset += node.text!.length;
      } else {
        offset += pos - start;
        found = true;
      }
    } else if (node.isBlock && nodePos > 0 && nodePos < pos) {
      offset += 1;
    }
    return true;
  });

  return offset;
}

async function fetchCompletion(
  prefix: string,
  suffix: string,
  signal: AbortSignal,
  maxTokens: number,
): Promise<string | null> {
  const { completionProvider, completionModel } = useAIStore.getState();
  let completion = "";
  for await (const chunk of api.stream(
    "/api/v1/ai/complete",
    {
      prefix,
      suffix,
      language: "docx",
      max_tokens: maxTokens,
      provider: completionProvider || undefined,
      model: completionModel || undefined,
    },
    signal,
  )) {
    try {
      const data = JSON.parse(chunk);
      if (data.text) completion += data.text;
    } catch {
      completion += chunk;
    }
  }
  const trimmed = completion.trim();
  return trimmed || null;
}

export const DocxGhostText = Extension.create({
  name: "docxGhostText",

  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        const pluginState = ghostTextPluginKey.getState(editor.state);
        if (pluginState?.ghostText) {
          const { pos, text } = pluginState.ghostText;
          editor.chain().focus().insertContentAt(pos, text).run();

          const { tr } = editor.state;
          tr.setMeta(ghostTextPluginKey, { clear: true });
          editor.view.dispatch(tr);
          return true;
        }

        if (editor.can().sinkListItem("listItem")) {
          return editor.chain().focus().sinkListItem("listItem").run();
        }
        if (editor.can().sinkListItem("taskItem")) {
          return editor.chain().focus().sinkListItem("taskItem").run();
        }

        editor.chain().focus().insertContent("\t").run();
        return true;
      },
      "Shift-Tab": ({ editor }) => {
        if (editor.can().liftListItem("listItem")) {
          return editor.chain().focus().liftListItem("listItem").run();
        }
        if (editor.can().liftListItem("taskItem")) {
          return editor.chain().focus().liftListItem("taskItem").run();
        }
        return false;
      },
      Escape: ({ editor }) => {
        const pluginState = ghostTextPluginKey.getState(editor.state);
        if (!pluginState?.ghostText) return false;

        const { tr } = editor.state;
        tr.setMeta(ghostTextPluginKey, { clear: true });
        editor.view.dispatch(tr);
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;

    function cancel() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (controller) {
        controller.abort();
        controller = null;
      }
    }

    return [
      new Plugin({
        key: ghostTextPluginKey,

        state: {
          init() {
            return {
              ghostText: null as { pos: number; text: string } | null,
              decorations: DecorationSet.empty,
            };
          },

          apply(tr, value) {
            const meta = tr.getMeta(ghostTextPluginKey);

            if (meta?.clear) {
              return { ghostText: null, decorations: DecorationSet.empty };
            }

            if (meta?.set) {
              const { pos, text } = meta.set;
              const widget = document.createElement("span");
              widget.className = "docx-ghost-text";
              widget.textContent = text;
              const deco = DecorationSet.create(tr.doc, [
                Decoration.widget(pos, widget, { side: 1 }),
              ]);
              return { ghostText: { pos, text }, decorations: deco };
            }

            if (tr.docChanged || tr.selectionSet) {
              return { ghostText: null, decorations: DecorationSet.empty };
            }

            if (value.ghostText) {
              return {
                ...value,
                decorations: value.decorations.map(tr.mapping, tr.doc),
              };
            }

            return value;
          },
        },

        props: {
          decorations(state) {
            return this.getState(state)?.decorations ?? DecorationSet.empty;
          },
        },

        view() {
          return {
            update(view, prevState) {
              const store = useCompletionStore.getState();
              if (!store.enabled) return;
              if (view.state.doc.eq(prevState.doc)) return;

              cancel();

              const { state } = view;
              const pos = state.selection.$head.pos;
              const fullText = getPlainText(state.doc);
              const charOffset = getTextOffsetAtPos(state.doc, pos);

              const prefix = fullText.slice(0, charOffset);
              const suffix = fullText.slice(charOffset);

              if (prefix.length < 10) return;

              const debounceMs = AGGRESSIVENESS_DEBOUNCE[store.aggressiveness];
              const maxTokens = LENGTH_MAX_TOKENS[store.maxLength];

              timer = setTimeout(async () => {
                const ac = new AbortController();
                controller = ac;

                try {
                  const text = await fetchCompletion(
                    prefix.slice(-3000),
                    suffix.slice(0, 1500),
                    ac.signal,
                    maxTokens,
                  );
                  if (ac.signal.aborted) return;
                  if (!text) return;

                  const currentPos = view.state.selection.$head.pos;
                  if (currentPos !== pos) return;

                  const { tr } = view.state;
                  tr.setMeta(ghostTextPluginKey, { set: { pos, text } });
                  view.dispatch(tr);
                } catch {
                  // aborted or network error
                }
              }, debounceMs);
            },

            destroy() {
              cancel();
            },
          };
        },
      }),
    ];
  },
});
